import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { Link } from 'react-router-dom';
import { 
  GraduationCap, Search, Plus, Phone, Mail, Trash2, Loader2, X, 
  Video, Users, MapPin, Euro, CheckCircle, Upload, AlertCircle, Maximize2 
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SEO from '../components/SEO';
import LoadingScreen from '../components/LoadingScreen';

// COSTANTI SICUREZZA
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_TITLE_LENGTH = 60;
const MAX_DESC_LENGTH = 1000;
const MAX_SUBJ_LENGTH = 50;
const ADMIN_EMAIL = 'info@tusciahub.it'; 

const Tutoring = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Filtri
  const [selectedMethod, setSelectedMethod] = useState('Tutti');
  const [searchTerm, setSearchTerm] = useState('');
  const methods = ['Tutti', 'Online', 'In Presenza', 'Ibrido'];

  // Stati Modali
  const [isSellOpen, setIsSellOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileError, setFileError] = useState(false);
  
  const [toast, setToast] = useState(null);

  const [newItem, setNewItem] = useState({ 
    title: '', description: '', price: '', method: 'Online', 
    subject: '', contact_info: '', contact_type: 'whatsapp', 
    image: null, termsAccepted: false 
  });

  useEffect(() => {
    const init = async () => {
        setLoading(true);
        await checkUser();
        await fetchItems();
        setLoading(false);
    };
    init();
  }, []);

  // --- FIX SCROLL ---
  useEffect(() => {
    if (isSellOpen || selectedItem) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => { 
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isSellOpen, selectedItem]);

  const checkUser = async () => { 
      const { data: { user } } = await supabase.auth.getUser(); 
      setUser(user); 
      if (user && user.email === ADMIN_EMAIL) {
          setIsAdmin(true);
      }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchItems = async () => {
    try {
      const { data, error } = await supabase
        .from('tutoring_items')
        .select('*, profiles(email)')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setItems(data || []);
    } catch (error) { 
      console.error(error); 
    }
  };

  const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
          showToast("Solo immagini (JPG, PNG).", "error");
          e.target.value = null;
          return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
          setFileError(true);
          setNewItem({ ...newItem, image: null });
          e.target.value = null;
      } else {
          setFileError(false);
          setNewItem({ ...newItem, image: file });
      }
  };

  const handleAddItem = async (e) => {
    e.preventDefault(); 
    if (!user) return showToast("Devi accedere per pubblicare!", "error");
    if (!newItem.termsAccepted) return showToast("Accetta i termini.", "error");
    if (fileError) return showToast("File troppo grande.", "error");

    if (newItem.title.length > MAX_TITLE_LENGTH) return showToast("Titolo troppo lungo.", "error");
    if (newItem.description.length > MAX_DESC_LENGTH) return showToast("Descrizione troppo lunga.", "error");
    if (newItem.subject.length > MAX_SUBJ_LENGTH) return showToast("Materia troppo lunga.", "error");
    
    setIsSubmitting(true);
    
    try {
      let publicUrl = null;
      
      if (newItem.image) {
          const fileExt = newItem.image.name.split('.').pop();
          const fileName = `${user.id}/${Date.now()}_tutor.${fileExt}`;
          
          const { error: uploadError } = await supabase.storage
            .from('tutoring_images')
            .upload(fileName, newItem.image);
          
          if (uploadError) throw uploadError;
          
          const res = supabase.storage.from('tutoring_images').getPublicUrl(fileName);
          publicUrl = res.data.publicUrl;
      }
      
      const { error: dbError } = await supabase.from('tutoring_items').insert([{ 
        title: newItem.title, description: newItem.description, 
        price: parseFloat(newItem.price || 0), method: newItem.method, 
        subject: newItem.subject, contact_info: newItem.contact_info, 
        contact_type: newItem.contact_type, image_url: publicUrl, 
        user_id: user.id 
      }]);

      if (dbError) throw dbError;
      
      showToast("Annuncio pubblicato!", "success");
      setIsSellOpen(false); 
      setNewItem({ title: '', description: '', price: '', method: 'Online', subject: '', contact_info: '', contact_type: 'whatsapp', image: null, termsAccepted: false });
      setFileError(false);
      await fetchItems();

    } catch (error) { showToast("Errore: " + error.message, "error"); } 
    finally { setIsSubmitting(false); }
  };

  const handleDelete = async (itemId, imageUrl) => { 
      if(!window.confirm("Sicuro di voler eliminare?")) return; 
      
      try { 
          if (imageUrl) {
              const fileUrlParts = imageUrl.split('/tutoring_images/');
              if (fileUrlParts.length > 1) {
                  const filePath = fileUrlParts[1];
                  const { error: storageError } = await supabase.storage
                      .from('tutoring_images') 
                      .remove([filePath]);
                  if (storageError) console.error("Errore cancellazione file:", storageError);
              }
          }

          const { error } = await supabase.from('tutoring_items').delete().eq('id', itemId);
          if (error) throw error;

          setItems(items.filter(i => i.id !== itemId)); 
          showToast("Annuncio eliminato.", "success");
          if(selectedItem?.id === itemId) setSelectedItem(null); 

      } catch(err) { showToast("Errore cancellazione.", "error"); } 
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          item.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMethod = selectedMethod === 'Tutti' || item.method === selectedMethod;
    return matchesSearch && matchesMethod;
  });

  const inputStyle = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-sm font-medium text-slate-700 placeholder:text-slate-400 focus:bg-white focus:ring-2 focus:ring-purple-200 focus:border-purple-300 outline-none transition-all shadow-sm";

  if (loading) return <LoadingScreen />;

  return (
    <div className="relative min-h-screen bg-slate-100 pt-24 pb-20 px-6 font-sans text-slate-800">
      <SEO title="Ripetizioni" description="Tutor e aiuto studio." url="/ripetizioni" />
      {/* Sfondo Decorativo VIOLA */}
      <div className="absolute top-0 right-[-10%] w-[500px] h-[500px] bg-purple-200/40 rounded-full blur-[100px] mix-blend-multiply pointer-events-none"></div>
      
      {toast && (
        <div className={`fixed bottom-10 left-1/2 -translate-x-1/2 z-[10000] animate-in slide-in-from-bottom-5 fade-in duration-300 px-6 py-4 rounded-xl shadow-2xl flex items-center gap-4 border min-w-[320px] ${toast.type === 'error' ? 'bg-red-900 text-white border-red-800' : 'bg-slate-900 text-white border-slate-800'}`}>
            {toast.type === 'error' ? <AlertCircle className="text-red-400"/> : <CheckCircle className="text-green-400"/>}
            <span className="font-bold text-sm">{toast.message}</span>
        </div>
      )}

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>

      <div className="relative z-10 max-w-7xl mx-auto">
        <PageHeader title="Ripetizioni & Tutor" subtitle="Trova aiuto per i tuoi esami." breadcrumbs={[{label: 'Ripetizioni'}]} />

        {/* --- BARRA FILTRI --- */}
        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm mb-10">
            <div className="flex flex-wrap xl:flex-nowrap gap-6 items-center justify-between">
                
                <div className="flex flex-col md:flex-row gap-6 w-full xl:w-auto flex-grow min-w-0 items-center">
                    <div className="relative w-full md:w-[320px] shrink-0 group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-purple-500 transition-colors" size={20}/>
                        <input type="text" placeholder="Es. Analisi, Inglese..." className="w-full bg-slate-50 border border-slate-200 rounded-2xl py-3 pl-12 pr-4 font-bold outline-none focus:ring-2 focus:ring-purple-200 focus:bg-white transition-all text-sm group-hover:border-slate-300" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}/>
                    </div>

                    <div className="flex-1 w-full md:w-auto overflow-x-auto pb-4 custom-scrollbar min-w-0">
                        <div className="flex gap-2">
                            {methods.map(m => (
                                <button key={m} onClick={() => setSelectedMethod(m)} className={`px-5 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition border ${selectedMethod === m ? 'bg-purple-600 text-white border-purple-600 shadow-md' : 'bg-white border-slate-200 text-slate-500 hover:border-purple-200 hover:text-purple-600'}`}>{m}</button>
                            ))}
                        </div>
                    </div>
                </div>

                <button 
                    onClick={() => user ? setIsSellOpen(true) : showToast("Accedi per pubblicare!", "error")} 
                    className="w-full xl:w-auto bg-purple-600 hover:bg-purple-700 text-white px-8 py-3.5 rounded-xl font-bold shadow-lg shadow-purple-200 flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 active:scale-95 shrink-0 mt-2 xl:mt-0"
                >
                    <Plus size={20}/> Offri Ripetizioni
                </button>
            </div>
        </div>

        {/* --- GRIGLIA ITEMS --- */}
        {filteredItems.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredItems.map(item => (
                    // CARD VIOLA SFUMATA
                    <div key={item.id} onClick={() => setSelectedItem(item)} className="group bg-gradient-to-b from-white to-purple-50 rounded-[2.5rem] border-2 border-purple-100 shadow-xl hover:shadow-2xl hover:border-purple-300 hover:-translate-y-2 transition-all duration-300 cursor-pointer overflow-hidden flex flex-col h-full relative">
                        
                        {/* Header con Immagine */}
                        <div className="p-6 flex items-start justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-3xl bg-white border-2 border-purple-100 overflow-hidden flex items-center justify-center shrink-0 shadow-sm">
                                    {item.image_url ? (
                                        <img src={item.image_url} className="w-full h-full object-cover" alt="tutor" />
                                    ) : (
                                        <GraduationCap size={32} className="text-purple-300"/>
                                    )}
                                </div>
                                <div>
                                    <h3 className="font-black text-slate-900 text-xl leading-tight line-clamp-1 group-hover:text-purple-600 transition-colors">{item.title}</h3>
                                    <p className="text-sm font-bold text-slate-500 mt-1">{item.subject}</p>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 flex flex-wrap gap-2 mb-4">
                            <span className="px-3 py-1 rounded-lg text-xs font-bold bg-white text-purple-700 border border-purple-100 flex items-center gap-1 shadow-sm">
                                {item.method === 'Online' ? <Video size={12}/> : <Users size={12}/>} {item.method}
                            </span>
                        </div>

                        {/* BOX PREZZO VIBRANTE (Viola/Fucsia) */}
                        <div className="px-6 mb-4">
                            <div className="w-full bg-gradient-to-r from-purple-500 to-fuchsia-600 p-4 rounded-2xl text-white shadow-lg shadow-purple-200/50 flex flex-col items-center justify-center relative overflow-hidden group-hover:scale-[1.02] transition-transform duration-300">
                                <p className="text-[10px] font-bold opacity-90 uppercase tracking-widest mb-1 text-white/90">Tariffa Oraria</p>
                                <div className="flex items-center gap-1">
                                    <Euro size={18} strokeWidth={3} className="text-white opacity-80"/>
                                    <span className="text-2xl font-black tracking-tight text-white">{item.price}</span>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 pb-6 flex-grow">
                            <p className="text-slate-500 text-sm line-clamp-2 mb-4 leading-relaxed font-medium">{item.description}</p>
                        </div>
                        
                        <div className="mt-auto px-6 pb-6 pt-4 border-t border-purple-100 flex justify-between items-center bg-white/50">
                            <span className="text-sm font-bold text-purple-600 group-hover:underline flex items-center gap-1">
                                <Maximize2 size={16}/> Contatta
                            </span>
                            
                            {user && (user.id === item.user_id || isAdmin) && (
                                <button 
                                    onClick={(e) => { e.stopPropagation(); handleDelete(item.id, item.image_url); }} 
                                    className="bg-white text-red-500 p-2.5 rounded-xl hover:bg-red-50 transition z-20 border border-red-100 shadow-sm"
                                >
                                    <Trash2 size={18}/>
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        ) : (
            <div className="text-center py-24 bg-white border-2 border-dashed border-slate-200 rounded-[3rem]">
                <div className="bg-purple-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 text-purple-400 animate-pulse"><GraduationCap size={32}/></div>
                <h3 className="font-bold text-slate-900 text-lg">Nessun tutor trovato</h3>
                <p className="text-slate-400 text-sm mb-6">Prova a cercare un'altra materia.</p>
                <button onClick={() => user ? setIsSellOpen(true) : showToast("Accedi prima!", "error")} className="text-purple-600 font-bold bg-white border border-purple-200 px-6 py-2 rounded-full hover:bg-purple-50 transition shadow-sm">Offri Ripetizioni</button>
            </div>
        )}

        {/* --- MODALE DETTAGLIO --- */}
        {selectedItem && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setSelectedItem(null)}></div>
                <div className="relative z-10 bg-white w-full max-w-3xl max-h-[90vh] rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-300">
                    
                    {/* Header Modale */}
                    <div className="p-6 border-b border-slate-100 flex items-start justify-between bg-slate-50/50 shrink-0">
                        <div className="flex items-center gap-4">
                            <div className="w-20 h-20 rounded-2xl bg-white border border-slate-100 flex items-center justify-center overflow-hidden shrink-0 shadow-sm">
                                {selectedItem.image_url ? <img src={selectedItem.image_url} className="w-full h-full object-cover" alt="tutor" /> : <GraduationCap size={32} className="text-purple-300"/>}
                            </div>
                            <div>
                                <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wide mb-1 inline-block">{selectedItem.subject}</span>
                                <h2 className="text-2xl md:text-3xl font-black text-slate-900 leading-tight">{selectedItem.title}</h2>
                            </div>
                        </div>
                        <button onClick={() => setSelectedItem(null)} className="p-2 bg-white rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition shadow-sm border border-slate-100"><X size={20}/></button>
                    </div>

                    <div className="p-8 overflow-y-auto custom-scrollbar flex-1 pb-24 md:pb-8">
                        <div className="grid grid-cols-2 gap-4 mb-8">
                             <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 text-center">
                                <span className="text-xs font-bold text-slate-400 uppercase block mb-1">Prezzo</span>
                                <span className="font-black text-2xl text-purple-600">€{selectedItem.price}<span className="text-sm text-slate-400 font-medium">/h</span></span>
                             </div>
                             <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 text-center">
                                <span className="text-xs font-bold text-slate-400 uppercase block mb-1">Metodo</span>
                                <span className="font-bold text-slate-900">{selectedItem.method}</span>
                             </div>
                        </div>

                        <div className="prose prose-slate prose-lg text-slate-600 mb-10 leading-relaxed">
                            <h3 className="font-bold text-slate-900 mb-4">Descrizione</h3>
                            <p className="whitespace-pre-wrap">{selectedItem.description}</p>
                        </div>
                        
                        <div className="mt-auto">
                            <a 
                                href={selectedItem.contact_type === 'whatsapp' ? `https://wa.me/${selectedItem.contact_info}` : `mailto:${selectedItem.contact_info}`} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className={`w-full text-white py-4 rounded-xl text-lg font-bold flex items-center justify-center gap-2 transition shadow-xl hover:-translate-y-1 hover:shadow-purple-200 active:scale-95 ${selectedItem.contact_type === 'whatsapp' ? 'bg-green-500 hover:bg-green-600' : 'bg-slate-900 hover:bg-slate-800'}`}
                            >
                                {selectedItem.contact_type === 'whatsapp' ? <Phone size={24}/> : <Mail size={24}/>} 
                                {selectedItem.contact_type === 'whatsapp' ? 'Scrivigli su WhatsApp' : 'Invia una Email'}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* --- MODALE UPLOAD (Stile Housing, Colori Viola) --- */}
        {isSellOpen && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setIsSellOpen(false)}></div>
                <div className="relative z-10 bg-white w-full max-h-[90vh] md:max-w-lg rounded-[2.5rem] shadow-2xl flex flex-col animate-in zoom-in-95 duration-200 border border-white/20 p-8">
                    
                    <button onClick={() => setIsSellOpen(false)} className="absolute top-6 right-6 p-2 bg-slate-50 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition"><X size={20}/></button>
                    
                    <div className="mb-6 pr-10">
                        <h3 className="text-2xl font-black text-slate-900 mb-2">Offri Ripetizioni</h3>
                        <p className="text-slate-500 text-sm font-medium">Crea un annuncio per trovare studenti.</p>
                    </div>
                    
                    <div className="overflow-y-auto custom-scrollbar flex-1 -mr-4 pr-4">
                        <form onSubmit={handleAddItem} className="space-y-4 pb-2">
                            <div className="space-y-1">
                                <input type="text" required maxLength={MAX_TITLE_LENGTH} className={inputStyle} placeholder="Titolo (es. Ripetizioni Analisi 1)" value={newItem.title} onChange={e => setNewItem({...newItem, title: e.target.value})} />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                                    <select className="w-full bg-transparent px-3 text-sm font-bold outline-none text-slate-600 cursor-pointer" value={newItem.method} onChange={e => setNewItem({...newItem, method: e.target.value})}>
                                        {methods.filter(m=>m!=='Tutti').map(m => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                </div>
                                <div className="relative">
                                    <input type="number" required min="0" className={inputStyle} placeholder="Prezzo/h" value={newItem.price} onChange={e => setNewItem({...newItem, price: e.target.value})} />
                                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">€/h</span>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <input type="text" required maxLength={MAX_SUBJ_LENGTH} className={inputStyle} placeholder="Materia Specifica" value={newItem.subject} onChange={e => setNewItem({...newItem, subject: e.target.value})} />
                                <div className="flex bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                                    <select className="bg-slate-100 text-xs font-bold px-1 border-r border-slate-200 outline-none text-slate-500 cursor-pointer" value={newItem.contact_type} onChange={e => setNewItem({...newItem, contact_type: e.target.value})}>
                                        <option value="whatsapp">WA</option>
                                        <option value="email">@</option>
                                    </select>
                                    <input type="text" required className="w-full bg-transparent px-3 text-sm font-medium outline-none placeholder:text-slate-400" placeholder="Contatto" value={newItem.contact_info} onChange={e => setNewItem({...newItem, contact_info: e.target.value})} />
                                </div>
                            </div>

                            <textarea className={`${inputStyle} h-28 resize-none`} placeholder="Descrizione (esperienza, orari...)" maxLength={MAX_DESC_LENGTH} value={newItem.description} onChange={e => setNewItem({...newItem, description: e.target.value})}></textarea>
                            
                            <div className="relative group">
                                <input type="file" accept="image/*" className="hidden" id="img-upload-tutor" onChange={handleFileChange} />
                                <label htmlFor="img-upload-tutor" className={`flex flex-col items-center justify-center h-24 w-full border-2 border-dashed rounded-2xl transition-all group-hover:scale-[1.01] cursor-pointer ${fileError ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50 hover:bg-slate-100 hover:border-purple-300'}`}>
                                    {newItem.image ? (
                                        <div className="flex flex-col items-center text-purple-600 animate-in zoom-in">
                                            <CheckCircle size={24} className="mb-1"/>
                                            <span className="text-xs font-bold">{newItem.image.name}</span>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center text-slate-400 group-hover:text-purple-500 transition-colors">
                                            <div className="bg-white p-2 rounded-full shadow-sm mb-1"><Upload size={18}/></div>
                                            <span className="text-xs font-bold">{fileError ? "File troppo grande" : "Carica Foto (Opzionale)"}</span>
                                        </div>
                                    )}
                                </label>
                            </div>

                            <label className="flex items-center gap-3 p-2 cursor-pointer hover:bg-slate-50 rounded-lg transition">
                                <input type="checkbox" required className="w-4 h-4 rounded text-purple-600 focus:ring-purple-500 cursor-pointer" checked={newItem.termsAccepted} onChange={e => setNewItem({...newItem, termsAccepted: e.target.checked})}/>
                                <span className="text-xs text-slate-500 font-medium">Accetto i <Link to="/terms" className="text-purple-600 font-bold hover:underline" target="_blank">Termini e Condizioni</Link>.</span>
                            </label>

                            <button disabled={isSubmitting} type="submit" className="w-full bg-slate-900 text-white py-4 rounded-xl font-bold text-lg hover:bg-purple-600 transition shadow-xl shadow-slate-200 active:scale-95 flex items-center justify-center gap-2">
                                {isSubmitting ? <Loader2 className="animate-spin"/> : 'Pubblica Annuncio'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default Tutoring;